/**
 * Patterns & Spec — a static reference page.
 *
 * Documents the conventions the other screens follow: kobo→₦, bps→%,
 * snapshot vs windowed, KPI card anatomy, chart patterns, states. Purely
 * informational; no data fetching.
 */

import { escape, fmtBps, fmtKobo, kpi, snapshotKpi } from './common.js';

export function renderSpec(host /* , ctx */) {
  host.innerHTML = `
    <div class="adm2-section-label">Conventions</div>
    <div class="adm2-panel">
      <div class="adm2-panel-title">Money &amp; rates</div>
      <div class="adm2-panel-sub">how the API talks to the UI</div>
      <div style="display:grid;grid-template-columns:1fr;gap:10px;margin-top:14px;">
        ${_row('kobo → ₦', 'Integer kobo ÷ 100, formatted with the Naira symbol. Never floats.')}
        ${_row('bps → %', 'Basis points ÷ 100. 5000 bps = 50%. Displayed with one decimal.')}
        ${_row('snapshot', 'Point-in-time balances (outstanding liability, wallet total, platform revenue). Never windowed.')}
        ${_row('windowed', 'Aggregate over the selected date range. Everything in Phase C endpoints.')}
      </div>
    </div>

    <div class="adm2-section-label">KPI card anatomy</div>
    <div class="adm2-two">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Windowed KPI</div>
        <div class="adm2-panel-sub">the default. purple rail on the hero variant.</div>
        <div style="margin-top:14px;display:grid;grid-template-columns:1fr;gap:12px;">
          ${kpi({ label: 'Net revenue', value: fmtKobo(4182650_00), hint: '30-day total' })}
          ${kpi({ label: 'Take rate', value: fmtBps(1240), hint: 'rake ÷ handle' })}
        </div>
      </div>
      <div class="adm2-panel">
        <div class="adm2-panel-title">Snapshot KPI</div>
        <div class="adm2-panel-sub">warm gold rail + SNAPSHOT pill. Ignores the date range.</div>
        <div style="margin-top:14px;display:grid;grid-template-columns:1fr;gap:12px;">
          ${snapshotKpi({ label: 'Wallet liability', value: fmtKobo(24_610_800_00), hint: 'owed to users' })}
          ${snapshotKpi({ label: 'Outstanding 50/50', value: fmtKobo(612_500_00), hint: 'unspent power-ups owed' })}
        </div>
      </div>
    </div>

    <div class="adm2-section-label">Chart patterns</div>
    <div class="adm2-panel">
      <div class="adm2-panel-title">Which chart for which metric</div>
      <div class="adm2-panel-sub">reuse these primitives; don't invent new chart types.</div>
      <div style="display:grid;grid-template-columns:1fr;gap:10px;margin-top:14px;">
        ${_row('Line / area trend', 'Anything over time — revenue, flows, duels created.')}
        ${_row('Diverging bars', 'Deposits vs withdrawals — top/bottom of the axis.')}
        ${_row('Funnel', 'Duel lifecycle · live-event participants.')}
        ${_row('Composition bars', 'Handle → prizes back + rake + power-ups − fees.')}
        ${_row('Snapshot cards', 'Wallet balance · liability · platform revenue.')}
      </div>
    </div>

    <div class="adm2-section-label">Screen → endpoint map</div>
    <div class="adm2-panel">
      <div class="adm2-panel-title">Every screen documented</div>
      <div class="adm2-panel-sub">reference for future contributors</div>
      <div style="margin-top:14px;">
        ${_mapRow('Overview', '/admin/metrics/overview + /financial + /reconciliation')}
        ${_mapRow('Revenue &amp; Earnings', '/admin/metrics/earnings (CSV via ?format=csv)')}
        ${_mapRow('Financial &amp; Ops', '/admin/metrics/financial (CSV added for v2) + /reconciliation')}
        ${_mapRow('Players (list)', '/admin/metrics + /admin/users + blacklist mutations')}
        ${_mapRow('Player detail', '/admin/metrics/user/{id} (NEW for v2)')}
        ${_mapRow('Duels', '/admin/metrics/duels (CSV via ?format=csv)')}
        ${_mapRow('Friends &amp; Referrals', '/admin/metrics/friends (CSV via ?format=csv)')}
        ${_mapRow('Game Health', '/admin/metrics/game-health + /content-quality')}
        ${_mapRow('Power-Ups', '/admin/metrics/powerups (CSV via ?format=csv)')}
        ${_mapRow('Live Events', '/admin/live + /admin/live/{id}/payouts')}
      </div>
    </div>
  `;
}

function _row(label, text) {
  return `
    <div style="display:flex;align-items:flex-start;gap:14px;padding:12px 15px;background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;">
      <span style="font:600 12px 'IBM Plex Mono',monospace;color:var(--a-brand);flex:none;min-width:110px;">${escape(label)}</span>
      <span style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);line-height:1.55;">${text}</span>
    </div>
  `;
}
function _mapRow(screen, endpoints) {
  return `
    <div style="display:flex;align-items:center;gap:14px;padding:11px 0;border-bottom:1px solid var(--a-border);">
      <span style="flex:1;font:500 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${screen}</span>
      <span style="font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${endpoints}</span>
    </div>
  `;
}
